import { useEffect, useMemo, useState } from 'react';
import { measureTextWidth, type TextWidthMeasurer } from 'utils/TextMeasurement';

// Font of the Autocomplete input as themed in ThemeProvider (MuiInputBase at 14px Nunito).
const AUTOCOMPLETE_INPUT_FONT = '14px Nunito, Helvetica, Arial, sans-serif';
// Small outlined input padding (6 + 8 + 4), the clear and popup indicator slots (65), and sub-pixel slack (4).
const AUTOCOMPLETE_INPUT_HORIZONTAL_SPACE = 87;

interface AutocompleteWidthBounds {
  minWidth?: number;
  maxWidth?: number;
}

/**
 * Width that fits the longest option label in a small outlined Autocomplete without truncation.
 * The open list takes the same width and its rows need less horizontal room than the input,
 * so the options stay on one line as well.
 */
export function getAutocompleteWidth(
  options: readonly { label: string }[] = [],
  { minWidth = 300, maxWidth = 500 }: AutocompleteWidthBounds = {},
  measureText: TextWidthMeasurer = measureTextWidth,
) {
  let width = minWidth;

  for (const option of options) {
    width = Math.max(width, measureText(option.label, AUTOCOMPLETE_INPUT_FONT) + AUTOCOMPLETE_INPUT_HORIZONTAL_SPACE);
    if (width >= maxWidth) return maxWidth;
  }

  return Math.ceil(width);
}

function isInputFontLoaded() {
  return !document.fonts || document.fonts.check(AUTOCOMPLETE_INPUT_FONT);
}

export function useAutocompleteWidth(
  options: readonly { label: string }[] | undefined,
  { minWidth, maxWidth }: AutocompleteWidthBounds = {},
) {
  // Canvas measures with a fallback font until Nunito has loaded, so measure again once it has.
  const [isFontLoaded, setIsFontLoaded] = useState(isInputFontLoaded);

  useEffect(() => {
    if (isFontLoaded) return;

    let isMounted = true;
    const handleFontLoaded = () => isMounted && setIsFontLoaded(true);
    document.fonts.load(AUTOCOMPLETE_INPUT_FONT).then(handleFontLoaded, handleFontLoaded);

    return () => {
      isMounted = false;
    };
  }, [isFontLoaded]);

  return useMemo(
    () => getAutocompleteWidth(options, { minWidth, maxWidth }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, minWidth, maxWidth, isFontLoaded],
  );
}
